import {
  Card,
  CardContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@material-ui/core";
import { Cached } from "@material-ui/icons";
import React from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  fetchListMachines,
  fetchSortMachineInfos,
  selector as remoteMaintenanceStoreSelector,
} from "../../../store/RemoteMaintenanceStore";
import { ProgressView } from "../../share";
import useStyles from "../useStyles";

import RemoteMaintenanceTableRow from "./RemoteMaintenanceTableRow";

const iconColor = "inherit";
const iconSize = "large";
const iconEdge = "end";

/**
 * RemoteMaintenanceCardコンポーネント.
 *
 * @return {object} コンポーネント
 */
function RemoteMaintenanceCard() {
  // スタイル
  const styles = useStyles();

  // Dispatch
  const dispatch = useDispatch();

  // 機器情報一覧のソート状態State (order: ソート種別, key: ソート対象列名)
  const [sortState, setSortState] = React.useState({
    order: "asc", // 降順 ("desc") / 昇順 ("asc")
    key: "iotName",
  });

  // Store情報
  const {
    // 読み込み中フラグ (true: 表示 / false: 非表示)
    isLoading,
    // 機器情報一覧
    machineInfos,
  } = useSelector(remoteMaintenanceStoreSelector);

  // 副作用 (ページ読み込み時)
  React.useEffect(() => {
    // 機器情報一覧の取得
    dispatch(fetchListMachines());
    // ソート設定をデフォルトに設定
    setSortState({
      order: "asc",
      key: "iotName",
    });
  }, [dispatch]);

  // リロードボタン押下時の処理
  const handleClickReloadButton = () => {
    // 機器情報一覧の取得
    dispatch(fetchListMachines());
    // ソート設定をデフォルトに設定
    setSortState({
      order: "asc",
      key: "iotName",
    });
  };

  // ソート変更時の処理
  const handleClickSortColumn = (column) => {
    const isDesc = column === sortState.key && sortState.order === "desc";
    const nextOrder = isDesc ? "asc" : "desc";
    const payload = { column, nextOrder };
    setSortState({
      order: nextOrder,
      key: column,
    });
    dispatch(fetchSortMachineInfos(payload));
  };

  return (
    <>
      <Card className={styles.card}>
        <CardContent className={`${styles.cardContent} ${styles.flexStart}`}>
          <div className={styles.marginLeftAuto}>
            <IconButton
              color={iconColor}
              edge={iconEdge}
              disabled={!machineInfos || isLoading}
              onClick={() => handleClickReloadButton()}
            >
              <Cached fontSize={iconSize} />
            </IconButton>
          </div>
        </CardContent>
        <CardContent className={styles.cardContent}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  className={`${styles.tableHeader} ${styles.machineNameTableCell}`}
                  key="table-header-col-iot_name"
                  sortDirection={
                    sortState.key === "iotName" ? sortState.order : false
                  }
                >
                  <TableSortLabel
                    active={sortState.key === "iotName"}
                    direction={sortState.order}
                    onClick={() => handleClickSortColumn("iotName")}
                  >
                    機器名
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  className={`${styles.tableHeader} ${styles.machineNameTableCell}`}
                  key="table-header-col-label"
                  sortDirection={
                    sortState.key === "label" ? sortState.order : false
                  }
                >
                  <TableSortLabel
                    active={sortState.key === "label"}
                    direction={sortState.order}
                    onClick={() => handleClickSortColumn("label")}
                  >
                    拠点名
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  className={`${styles.tableHeader} ${styles.machineTableCell}`}
                  key="table-header-col-power"
                  sortDirection={
                    sortState.key === "power" ? sortState.order : false
                  }
                >
                  <TableSortLabel
                    active={sortState.key === "power"}
                    direction={sortState.order}
                    onClick={() => handleClickSortColumn("power")}
                  >
                    電源状態
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  className={`${styles.tableHeader} ${styles.machineTableCell}`}
                  key="table-header-col-remote_access"
                  sortDirection={
                    sortState.key === "remoteAccess" ? sortState.order : false
                  }
                >
                  <TableSortLabel
                    active={sortState.key === "remoteAccess"}
                    direction={sortState.order}
                    onClick={() => handleClickSortColumn("remoteAccess")}
                  >
                    リモート接続
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  className={`${styles.tableHeader} ${styles.machineTableCell}`}
                  key="table-header-col-enable_time"
                  sortDirection={
                    sortState.key === "enableTime" ? sortState.order : false
                  }
                >
                  <TableSortLabel
                    active={sortState.key === "enableTime"}
                    direction={sortState.order}
                    onClick={() => handleClickSortColumn("enableTime")}
                  >
                    有効期限
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  className={`${styles.tableHeader} ${styles.machineTableCell}`}
                  key="table-header-col-client_ip"
                  sortDirection={
                    sortState.key === "clientIp" ? sortState.order : false
                  }
                >
                  <TableSortLabel
                    active={sortState.key === "clientIp"}
                    direction={sortState.order}
                    onClick={() => handleClickSortColumn("clientIp")}
                  >
                    接続元IPアドレス
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  className={`${styles.tableHeader} ${styles.machineTableCell}`}
                  key="table-header-col-box_ip"
                  sortDirection={
                    sortState.key === "boxIp" ? sortState.order : false
                  }
                >
                  <TableSortLabel
                    active={sortState.key === "boxIp"}
                    direction={sortState.order}
                    onClick={() => handleClickSortColumn("boxIp")}
                  >
                    接続先IPアドレス
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <ProgressView />
                  </TableCell>
                </TableRow>
              ) : (
                <RemoteMaintenanceTableRow />
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </>
  );
}

export default RemoteMaintenanceCard;
